import { Link } from 'react-router-dom';
import MarketingLayout from '../../components/MarketingLayout';
import { appLinks } from '../../lib/crossSiteLinks';
import { tokens } from '../../components/SEOPageLayout';
import { BreadcrumbJsonLd, FaqJsonLd } from '../../components/StructuredData';
import { SeoLongformSections } from '../../components/SeoLongformSections';

export default function OutsourcedSoftwareChangeControlPage() {
  const faqs = [
    {
      question: 'Does EvidentTrail require access to contractor systems?',
      answer: 'No. Evidence is collected from your GitHub repositories with read-only access.'
    },
    {
      question: 'Do you store source code?',
      answer: 'No. Evidence packs are metadata-only and do not include code content or diffs.'
    },
    {
      question: 'Does this certify compliance?',
      answer: 'No. It provides evidence packs that support audit reviews.'
    },
    {
      question: 'Can I generate a one-off report?',
      answer: 'Yes. Guest mode supports one report for one repository with short retention.'
    }
  ];

  const longformSections = [
    {
      title: 'What auditors ask for',
      paragraphs: [
        'Outsourced development audits focus on evidence that approvals and required checks were enforced regardless of who authored the code.'
      ],
      bullets: [
        'Approval evidence before merges',
        'Required check status metadata',
        'Branch protection settings in effect'
      ]
    },
    {
      title: 'Evidence artifacts included',
      paragraphs: [
        'Evidence packs include approvals, CI check status metadata, branch protection snapshots, and exception records when present.'
      ],
      bullets: [
        'PR approvals and reviewer identities',
        'Required checks status metadata',
        'Branch protection rulesets',
        'Exception records for bypasses'
      ]
    },
    {
      title: 'How EvidentTrail works (read-only)',
      paragraphs: [
        'EvidentTrail connects to your GitHub org with read-only access and captures metadata only. It does not integrate with contractor tooling.'
      ],
      bullets: [
        'Read-only GitHub App access',
        'Metadata-only evidence capture',
        'Exportable packs with checksums'
      ]
    },
    {
      title: 'How to interpret the output',
      paragraphs: [
        'Use approvals and required checks to show that outsourced changes followed your internal control process.'
      ],
      bullets: [
        'Approvals confirm authorization',
        'Checks show testing evidence',
        'Checksums validate export integrity'
      ]
    },
    {
      title: 'Common failure modes',
      paragraphs: [
        'Vendor-provided screenshots or missing approvals create gaps. Evidence packs provide an independent trail.'
      ],
      bullets: [
        'Approvals missing for contractor changes',
        'Branch protection rules not documented',
        'Evidence without a checksum trail'
      ]
    }
  ];
  return (
    <MarketingLayout
      title="Change Control Evidence for Outsourced Software Development"
      description="Prove control over software changes when development is outsourced. Independent, read-only evidence collection from GitHub. Continuous proof vs point-in-time snapshots."
      h1="Change Control Evidence for Outsourced Development"
      heroText="When software development is outsourced, proving control becomes harder. EvidentTrail provides independent, read-only evidence collection that works regardless of who writes the code."
      type="article"
    >
      <BreadcrumbJsonLd
        items={[
          { name: 'Home', url: 'https://evidenttrail.com/' },
          { name: 'Outsourced Development', url: 'https://evidenttrail.com/outsourced-software-change-control' }
        ]}
      />
      <FaqJsonLd items={faqs} />
      {/* The Challenge */}
      <section className={tokens.sectionAlt}>
        <div className={tokens.container}>
          <div className="max-w-3xl mx-auto">
            <h2 className={`${tokens.heading.h2} mb-6`}>The Challenge with Outsourced Development</h2>
            <p className={`${tokens.text.base} mb-6`}>
              Auditors and enterprise customers often ask: "How do you control changes when
              development is done by third parties?" Common concerns include:
            </p>
            <div className={tokens.card}>
              <ul className="space-y-4">
                <li className="flex items-start gap-3">
                  <span className="w-2 h-2 bg-amber-400 rounded-full mt-2 flex-shrink-0" />
                  <span className={tokens.text.base}>
                    <strong className="text-white">Trust but verify</strong> — Contractors may follow your process,
                    but you need independent proof
                  </span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="w-2 h-2 bg-amber-400 rounded-full mt-2 flex-shrink-0" />
                  <span className={tokens.text.base}>
                    <strong className="text-white">Limited visibility</strong> — You may not be in every Slack channel
                    or present for every code review
                  </span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="w-2 h-2 bg-amber-400 rounded-full mt-2 flex-shrink-0" />
                  <span className={tokens.text.base}>
                    <strong className="text-white">Audit sampling</strong> — When auditors sample changes,
                    you need to prove control for any random selection
                  </span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="w-2 h-2 bg-amber-400 rounded-full mt-2 flex-shrink-0" />
                  <span className={tokens.text.base}>
                    <strong className="text-white">Supplier transitions</strong> — Evidence needs to persist
                    even if you change development partners
                  </span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      <SeoLongformSections sections={longformSections} faqs={faqs} />

      {/* Independent Evidence */}
      <section className={tokens.section}>
        <div className={tokens.container}>
          <div className="max-w-3xl mx-auto">
            <h2 className={`${tokens.heading.h2} mb-6`}>Independent, Read-Only Evidence</h2>
            <p className={`${tokens.text.base} mb-8`}>
              EvidentTrail connects to your GitHub organization — not your contractor's systems.
              This provides several advantages:
            </p>
            <div className="grid md:grid-cols-2 gap-6">
              <div className={tokens.card}>
                <div className="w-12 h-12 bg-primary-600/20 rounded-lg flex items-center justify-center mb-4">
                  <svg className="w-6 h-6 text-primary-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                  </svg>
                </div>
                <h3 className={`${tokens.heading.h3} mb-2`}>Your Data, Your Control</h3>
                <p className={tokens.text.base}>
                  Evidence is collected from repositories you own, not from third-party reports.
                </p>
              </div>
              <div className={tokens.card}>
                <div className="w-12 h-12 bg-primary-600/20 rounded-lg flex items-center justify-center mb-4">
                  <svg className="w-6 h-6 text-primary-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                  </svg>
                </div>
                <h3 className={`${tokens.heading.h3} mb-2`}>Read-Only Access</h3>
                <p className={tokens.text.base}>
                  We never modify your repositories, workflows, or CI pipelines.
                </p>
              </div>
              <div className={tokens.card}>
                <div className="w-12 h-12 bg-primary-600/20 rounded-lg flex items-center justify-center mb-4">
                  <svg className="w-6 h-6 text-primary-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <h3 className={`${tokens.heading.h3} mb-2`}>Continuous Collection</h3>
                <p className={tokens.text.base}>
                  Evidence is captured as changes happen, not assembled after the fact.
                </p>
              </div>
              <div className={tokens.card}>
                <div className="w-12 h-12 bg-primary-600/20 rounded-lg flex items-center justify-center mb-4">
                  <svg className="w-6 h-6 text-primary-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                  </svg>
                </div>
                <h3 className={`${tokens.heading.h3} mb-2`}>Supplier Independent</h3>
                <p className={tokens.text.base}>
                  Change contractors without losing your audit trail.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Continuous vs Point-in-Time */}
      <section className={tokens.sectionAlt}>
        <div className={tokens.container}>
          <div className="max-w-3xl mx-auto">
            <h2 className={`${tokens.heading.h2} mb-6`}>Continuous Proof vs Point-in-Time Snapshots</h2>
            <div className="grid md:grid-cols-2 gap-6">
              <div className={`${tokens.card} border-red-500/30`}>
                <h3 className={`${tokens.heading.h3} text-slate-400 mb-4`}>Point-in-Time (Traditional)</h3>
                <ul className="space-y-3">
                  <li className="flex items-start gap-2">
                    <svg className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                    <span className={tokens.text.base}>Evidence gathered only during audit prep</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <svg className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                    <span className={tokens.text.base}>Missing evidence for periods between samples</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <svg className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                    <span className={tokens.text.base}>Relies on contractor-provided reports</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <svg className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                    <span className={tokens.text.base}>Gaps discovered during audit, not before</span>
                  </li>
                </ul>
              </div>
              <div className={`${tokens.card} border-primary-500/30`}>
                <h3 className={`${tokens.heading.h3} text-primary-400 mb-4`}>Continuous (EvidentTrail)</h3>
                <ul className="space-y-3">
                  <li className="flex items-start gap-2">
                    <svg className="w-5 h-5 text-primary-400 flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                    <span className={tokens.text.base}>Evidence captured as changes happen</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <svg className="w-5 h-5 text-primary-400 flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                    <span className={tokens.text.base}>Complete timeline for any audit period</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <svg className="w-5 h-5 text-primary-400 flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                    <span className={tokens.text.base}>Independent of contractor involvement</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <svg className="w-5 h-5 text-primary-400 flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                    <span className={tokens.text.base}>Drift detection alerts you to gaps in real-time</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className={tokens.section}>
        <div className={tokens.container}>
          <div className="max-w-2xl mx-auto text-center">
            <h2 className={`${tokens.heading.h2} mb-4`}>Prove Control Over Outsourced Development</h2>
            <p className={`${tokens.text.base} mb-8`}>
              Start collecting independent evidence from your GitHub repositories today.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <a href={appLinks.register} className="btn btn-primary text-lg px-8 py-3">
                Get Started
              </a>
              <Link to="/read-only-github-audit" className="text-slate-300 hover:text-white transition-colors">
                Learn about Read-Only Access →
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Related Topics */}
      <section className={`${tokens.section} pt-0`}>
        <div className={tokens.container}>
          <h2 className={`${tokens.heading.h3} mb-8 text-center`}>Related Solutions</h2>
          <div className="grid md:grid-cols-3 gap-6">
            <Link to="/read-only-github-audit" className={`${tokens.card} hover:border-primary-500/50 transition-colors`}>
              <h4 className="text-white font-medium mb-2">Read-Only Audit</h4>
              <p className="text-sm text-slate-400">How we connect securely without write permissions.</p>
            </Link>
            <Link to="/soc2-change-management-evidence" className={`${tokens.card} hover:border-primary-500/50 transition-colors`}>
              <h4 className="text-white font-medium mb-2">SOC 2 Evidence</h4>
              <p className="text-sm text-slate-400">Mapping your contractor's work to SOC 2 controls.</p>
            </Link>
            <Link to="/features/audit-timeline" className={`${tokens.card} hover:border-primary-500/50 transition-colors`}>
              <h4 className="text-white font-medium mb-2">Audit Timeline</h4>
              <p className="text-sm text-slate-400">See every change in a chronological audit log.</p>
            </Link>
          </div>
        </div>
      </section>
    </MarketingLayout>
  );
}
